// ── reference speeds (mirror player.ts) ─────────────────────────────────────
// Kept here so the pure feel math can normalise "how fast am I going" without a
// back-reference to the controller. Must track MOVE_SPEED * SPRINT_MULT.
const WALK_SPEED   = 2.6
const SPRINT_SPEED = 4.6

// ── step cadence ────────────────────────────────────────────────────────────
// A person takes faster steps the faster they move; a fixed 2 Hz bob reads as a
// metronome bolted to your eyes. Frequency tracks speed between these bounds.
const STEP_FREQ_WALK = 1.9  // Hz at walk speed
const STEP_FREQ_RUN  = 2.55 // Hz at sprint speed

// ── view-bob amplitudes (at full speed, motionScale = 1) ────────────────────
// The old bob rotated the CAMERA in PITCH, which is the single most reliable way
// to make people motion-sick. This bob translates the head instead: a vertical
// dip (never a rise) plus a subtle sideways sway and a whisker of roll.
const BOB_Y_AMP    = 0.045 // world units — vertical dip, at 2x step frequency
const BOB_X_AMP    = 0.035 // world units — lateral sway, at step frequency
const BOB_ROLL_AMP = 0.65  // degrees     — head roll, at step frequency

// ── landing dip ─────────────────────────────────────────────────────────────
const LAND_DIP_MAX     = 0.09  // world units — hard cap on the landing crouch
const LAND_DIP_PER_MPS = 0.014 // dip metres per m/s of downward impact
const LAND_ROLL_IMPULSE = 1.4  // degrees — tiny random roll kick on touchdown

// ── breathing idle sway ─────────────────────────────────────────────────────
// A dead-still camera reads as a paused game. This is a barely-there sway that
// fades IN after input stops, on two incommensurate frequencies so it never
// visibly loops.
const BREATH_RAMP    = 0.6   // seconds to fade breathing in
const BREATH_Y_AMP   = 0.006 // world units — vertical breath
const BREATH_YAW_AMP = 0.18  // degrees     — yaw drift
const BREATH_F1      = 0.83  // Hz — vertical
const BREATH_F2      = 0.57  // Hz — yaw (deliberately not a multiple of F1)

/**
 * VERTICAL fov (PlayCanvas default is vertical). 68 vertical ~= 100 horizontal
 * at 16:9 — the band shipped games settle in. The previous 90 vertical was
 * ~121 horizontal, which fisheyes every straight wall line.
 */
const FOV_BASE     = 68
// The FOV widen is driven by REAL speed now, not "is sprint held": tap forward
// at a jog and the world only leans out a touch. Capped small so it's a felt
// cue, not a fisheye lurch.
const FOV_KICK_MAX = 4  // extra vertical degrees at full sprint
const FOV_RATE_IN  = 9  // lerp speed widening (fast — the shove of accelerating)
const FOV_RATE_OUT = 5  // lerp speed narrowing (slower — settling back)

// ── acceleration model ──────────────────────────────────────────────────────
// Horizontal velocity RAMPS toward its target instead of snapping. The magnitude
// each case uses is chosen so, from rest, you reach the target in roughly:
//   WALK_ACCEL:   2.6 m/s in ~0.10 s
//   STOP_DECEL:   2.6 -> 0 in ~0.08 s
//   SPRINT_ACCEL: 2.6 -> 4.6 in ~0.18 s (momentum builds, doesn't teleport)
// In the air you have far less authority over your motion.
export const WALK_ACCEL     = 26   // m/s^2
export const STOP_DECEL     = 33   // m/s^2
export const SPRINT_ACCEL   = 12   // m/s^2
export const AIR_ACCEL_MULT = 0.4  // multiplier while not grounded

export function clampParam(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

/**
 * Step the horizontal velocity toward a target by at most `accel * dt`, moving
 * straight-line in velocity space so diagonal changes don't accelerate faster
 * than cardinal ones. Pure: no state, no side effects — the whole point is that
 * it's trivially testable (reaches target in accel-derived time).
 */
export function stepHorizontalVelocity(
  vx: number, vz: number,
  tx: number, tz: number,
  accel: number, dt: number,
): { x: number; z: number } {
  const dx = tx - vx
  const dz = tz - vz
  const dist = Math.sqrt(dx * dx + dz * dz)
  const maxStep = accel * Math.max(0, dt)
  if (dist <= maxStep || dist === 0) return { x: tx, z: tz }
  const s = maxStep / dist
  return { x: vx + dx * s, z: vz + dz * s }
}

export interface MovementFeedback {
  /** Retained for API stability; the pitch bob is gone, so this is always 0. */
  pitchOffsetDeg: number
  /** Vertical (world-Y) camera offset: bob dip + landing dip + breath. */
  yOffset: number
  /** Camera-LOCAL rightward offset in metres (apply along entity.right). */
  lateralOffset: number
  /** Camera roll in degrees (bob roll + decaying landing roll). */
  rollDeg: number
  /** Breathing yaw drift in degrees (added to look yaw). */
  yawOffsetDeg: number
  /** Vertical FOV in degrees. */
  fov: number
}

export interface MovementFeelInput {
  isWalking: boolean
  isSprinting: boolean
  isGrounded: boolean
  bobEnabled: boolean
  /** Horizontal speed in m/s. Falls back to walk/sprint reference if omitted. */
  speed?: number
  /** 0–1 accessibility intensity scale for all added motion (1 = full). */
  motionScale?: number
  /** Downward impact speed (m/s) on the frame of touchdown; 0 otherwise. */
  landingImpact?: number
}

export class MovementFeel {
  private bobPhase = 0
  private dip = 0
  private dipVel = 0
  private roll = 0
  private rollVel = 0
  private breathPhase1 = 0
  private breathPhase2 = 0
  private idleRamp = 0
  private fov = FOV_BASE

  constructor(private readonly rng: () => number = Math.random) {}

  update(dt: number, opts: MovementFeelInput): MovementFeedback {
    const t = clampParam(dt, 0, 0.1)
    const motion = clampParam(opts.motionScale ?? 1, 0, 1)
    const bobOn = opts.bobEnabled && motion > 0

    // Effective horizontal speed. Legacy callers pass no `speed`; fall back to
    // the sprint/walk reference so "is sprinting" still widens FOV in old tests.
    const spd = opts.speed ?? (opts.isSprinting ? SPRINT_SPEED : opts.isWalking ? WALK_SPEED : 0)
    const speedNorm = clampParam(spd / SPRINT_SPEED, 0, 1)

    // ── idle ramp (breathing envelope) ────────────────────────────────────
    // Fades in slowly (~0.6 s) when standing, collapses fast once you move.
    const idleTarget = !opts.isWalking && opts.isGrounded ? 1 : 0
    const idleRate = idleTarget > this.idleRamp ? t / BREATH_RAMP : t / 0.15
    this.idleRamp += (idleTarget - this.idleRamp) * clampParam(idleRate, 0, 1)

    // ── view bob ──────────────────────────────────────────────────────────
    let yOffset = 0
    let lateralOffset = 0
    let bobRoll = 0
    if (bobOn && opts.isWalking && opts.isGrounded) {
      const stepFreq = STEP_FREQ_WALK + (STEP_FREQ_RUN - STEP_FREQ_WALK) * speedNorm
      this.bobPhase += t * stepFreq * Math.PI * 2
      // Amplitude scales with speed AND the accessibility scale.
      const amp = speedNorm * motion
      // Vertical at 2x step frequency; (cos - 1) keeps it in [-amp, 0] so the
      // head only ever DIPS below neutral, never rises above it.
      yOffset += (Math.cos(this.bobPhase * 2) * 0.5 - 0.5) * BOB_Y_AMP * amp
      // Lateral + roll at step frequency (weight shifts side to side per stride).
      lateralOffset += Math.sin(this.bobPhase) * BOB_X_AMP * amp
      bobRoll += Math.sin(this.bobPhase) * BOB_ROLL_AMP * amp
    } else {
      // Ease the phase toward rest so bob doesn't resume mid-stride.
      this.bobPhase *= Math.max(0, 1 - t * 6)
    }

    // ── breathing idle sway ───────────────────────────────────────────────
    this.breathPhase1 += t * BREATH_F1 * Math.PI * 2
    this.breathPhase2 += t * BREATH_F2 * Math.PI * 2
    const breath = this.idleRamp * motion * (opts.bobEnabled ? 1 : 0)
    yOffset += Math.sin(this.breathPhase1) * BREATH_Y_AMP * breath
    const yawOffsetDeg = Math.sin(this.breathPhase2) * BREATH_YAW_AMP * breath

    // ── landing dip (impact-scaled spring) + random roll ──────────────────
    const impact = opts.landingImpact ?? 0
    if (impact > 0) {
      const dipMag = clampParam(impact * LAND_DIP_PER_MPS, 0, LAND_DIP_MAX)
      this.dipVel = -dipMag * 12
      this.rollVel += (this.rng() * 2 - 1) * LAND_ROLL_IMPULSE * motion
    }
    this.dipVel += -this.dip * 120 * t
    this.dipVel *= Math.max(0, 1 - 12 * t)
    this.dip += this.dipVel * t
    this.dip = clampParam(this.dip, -LAND_DIP_MAX, LAND_DIP_MAX * 0.5)
    yOffset += this.dip

    // ── landing roll spring (decays back to level) ────────────────────────
    this.rollVel += -this.roll * 90 * t
    this.rollVel *= Math.max(0, 1 - 10 * t)
    this.roll += this.rollVel * t
    this.roll = clampParam(this.roll, -3, 3)

    // ── FOV kick (real-speed driven, faster in than out) ──────────────────
    const kick = FOV_KICK_MAX
      * clampParam((spd - WALK_SPEED) / (SPRINT_SPEED - WALK_SPEED), 0, 1)
      * motion
    const targetFov = FOV_BASE + kick
    const rate = targetFov > this.fov ? FOV_RATE_IN : FOV_RATE_OUT
    this.fov += (targetFov - this.fov) * clampParam(t * rate, 0, 1)
    this.fov = clampParam(this.fov, FOV_BASE, FOV_BASE + FOV_KICK_MAX)

    return {
      pitchOffsetDeg: 0,
      yOffset: clampParam(
        yOffset,
        -(LAND_DIP_MAX + BOB_Y_AMP + BREATH_Y_AMP),
        BREATH_Y_AMP + LAND_DIP_MAX * 0.5,
      ),
      lateralOffset,
      rollDeg: bobRoll + this.roll,
      yawOffsetDeg,
      fov: this.fov,
    }
  }

  reset(): void {
    this.bobPhase     = 0
    this.dip          = 0
    this.dipVel       = 0
    this.roll         = 0
    this.rollVel      = 0
    this.breathPhase1 = 0
    this.breathPhase2 = 0
    this.idleRamp     = 0
    this.fov          = FOV_BASE
  }
}
